import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import SideNav from '../components/SideNav';
import { useNavigate } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import { debounce } from 'lodash';
import { Helmet } from 'react-helmet-async';
import { Popover, OverlayTrigger } from 'react-bootstrap';
import { LuCalendarClock } from 'react-icons/lu';
import CalendarWidget from '../components/review-calendar/CalendarWidget';
import ReviewCalendarPopover from '../components/review-calendar/ReviewCalendarPopover';
import MainLayout from '../components/MainLayout';
import LoadingQuote from '../components/widgets/LoadingQuote';
import {
  Container,
  Row,
  Col,
  Card,
  ProgressBar,
  Spinner,
  Toast,
  Modal,
  Button,
} from 'react-bootstrap';
import './GroupCycleReview.css';

const apiUrl = process.env.REACT_APP_API_URL;

const GroupReviewOverview = () => {
  const navigate = useNavigate();
  const { cycleId, groupId } = useParams(); // Assuming cycleId is passed as a URL parameter
  const [cycle, setCycle] = useState(null);
  const [selectedSubtask, setSelectedSubtask] = useState(null);
  const [checkedSubtask, setCheckedSubtask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const [subtaskChanges, setSubtaskChanges] = useState({});

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success'); // 'success' or 'danger'

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [activeKey, setActiveKey] = useState(null); // State to handle the active accordion item
  const [confettiOpacity, setConfettiOpacity] = useState(1);
  const [groupDetails, setGroupDetails] = useState();
  const [totalProgress, setTotalProgress] = useState();
  const [plannedDates, setPlannedDates] = useState({});
  const [completedDates, setCompletedDates] = useState({});
  const [subtaskData, setSubtaskData] = useState({});
  const [subtaskProgress, setSubtaskProgress] = useState({});
  const [totalGoalProgress, setTotalGoalProgress] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [needsProgressUpdate, setNeedsProgressUpdate] = useState(false);
  const [progressUpdateData, setProgressUpdateData] = useState({
    memberId: null,
    goalId: null,
    subtaskId: null,
  });
  const [selectedSubtaskForCalendar, setSelectedSubtaskForCalendar] =
    useState(null);
  const [expandedSubtasks, setExpandedSubtasks] = useState([]);

  // 4. Add useEffect to handle progress calculations after state updates
  // 4. Add useEffect to handle progress calculations after state updates
  useEffect(() => {
    if (needsProgressUpdate && cycle) {
      const { memberId, goalId, subtaskId } = progressUpdateData;

      // Define async function for calculations
      const performProgressCalculations = async () => {
        try {
          // Find the member in the cycle
          const member = cycle.cycleMembers.find((m) => m._id === memberId);

          if (!member) {
            console.error('Member not found:', memberId);
            return;
          }

          // Calculate subtask progress
          const subtaskProgress = calculateSubtaskProgressValue(
            subtaskId,
            member,
          );

          // Update the subtask in the member object
          let updatedGoal = null;
          for (const goal of member.goals) {
            for (const subtask of goal.subtasks) {
              if (subtask._id === subtaskId) {
                subtask.subtaskProgress = subtaskProgress;
                updatedGoal = goal;
                break;
              }
            }
            if (updatedGoal) break;
          }

          // Calculate goal progress
          if (updatedGoal) {
            const goalProgress = calculateGoalProgressValue(updatedGoal);
            updatedGoal.progress = goalProgress;
          }

          // Calculate member progress
          const progress = calculateMemberProgressValue(member);

          // Update the cycle state to store the progress in the member object
          setCycle((prevCycle) => ({
            ...prevCycle,
            cycleMembers: prevCycle.cycleMembers.map((m) => {
              if (m._id === memberId) {
                return {
                  ...m,
                  progressOfPerson: progress, // Store the calculated progress
                };
              }
              return m;
            }),
          }));
        } catch (error) {
          console.error('Error calculating progress:', error);
        } finally {
          // Reset the flag after calculations are done
          setNeedsProgressUpdate(false);
        }
      };

      performProgressCalculations();
    }
  }, [needsProgressUpdate, cycle, progressUpdateData]);
  // Debounced save function for calendar dates
  const debouncedSaveCalendarDates = debounce(
    async (cyclememberId, updatedMember, apiUrl, cycleId, callbacks) => {
      const { setToastMessage, setToastType, setShowToast } = callbacks;

      try {
        // Create a local copy of the updatedMember to avoid reference issues
        const memberToUpdate = JSON.parse(JSON.stringify(updatedMember));

        // Calculate progress using the helper functions
        // Find all subtask IDs in the memberToUpdate
        const subtaskIds = [];
        memberToUpdate.goals.forEach((goal) => {
          goal.subtasks.forEach((subtask) => {
            subtaskIds.push(subtask._id);
          });
        });

        // Calculate progress for all subtasks
        for (const subtaskId of subtaskIds) {
          // Update the subtask progress in our local copy
          const subtaskProgress = calculateSubtaskProgressValue(
            subtaskId,
            memberToUpdate,
          );

          // Find and update the subtask in our local copy
          for (const goal of memberToUpdate.goals) {
            for (const subtask of goal.subtasks) {
              if (subtask._id === subtaskId) {
                subtask.subtaskProgress = subtaskProgress;
                break;
              }
            }
          }
        }

        // Calculate progress for all goals in our local copy
        for (const goal of memberToUpdate.goals) {
          const goalProgress = calculateGoalProgressValue(goal);
          goal.progress = goalProgress;
        }

        // Calculate total progress for the member using our local copy
        const memberProgress = calculateMemberProgressValue(memberToUpdate);

        // Add progressOfPerson to the memberToUpdate object
        memberToUpdate.progressOfPerson = memberProgress;

        // Now send the updated member with progress included
        await axios.put(
          `${apiUrl}/cycles/${cycleId}/members/${cyclememberId}`,
          memberToUpdate,
          {
            headers: {
              'x-auth-token': localStorage.getItem('token'),
              'Content-Type': 'application/json',
            },
          },
        );

        // Update the local state with the updated progress
        setCycle((prevCycle) => ({
          ...prevCycle,
          cycleMembers: prevCycle.cycleMembers.map((member) =>
            member._id === cyclememberId
              ? { ...member, progressOfPerson: memberProgress }
              : member,
          ),
        }));
      } catch (error) {
        setToastMessage('Failed to save calendar dates');
        setToastType('error');
        setShowToast(true);
      }
    },
    1000,
  );

  // Create a debounced save function
  const debouncedSave = debounce(async (cyclememberId, updatedMember) => {
    try {
      // Create a local copy of the updatedMember to avoid reference issues
      const memberToUpdate = JSON.parse(JSON.stringify(updatedMember));

      // Find all subtask IDs in the memberToUpdate
      const subtaskIds = [];
      memberToUpdate.goals.forEach((goal) => {
        goal.subtasks.forEach((subtask) => {
          subtaskIds.push(subtask._id);
        });
      });

      // Calculate progress for all subtasks
      for (const subtaskId of subtaskIds) {
        // Update the subtask progress in our local copy
        const subtaskProgress = calculateSubtaskProgressValue(
          subtaskId,
          memberToUpdate,
        );

        // Find and update the subtask in our local copy
        for (const goal of memberToUpdate.goals) {
          for (const subtask of goal.subtasks) {
            if (subtask._id === subtaskId) {
              subtask.subtaskProgress = subtaskProgress;
              break;
            }
          }
        }
      }

      // Calculate progress for all goals in our local copy
      for (const goal of memberToUpdate.goals) {
        const goalProgress = calculateGoalProgressValue(goal);
        goal.progress = goalProgress;
      }

      // Calculate total progress for the member using our local copy
      const memberProgress = calculateMemberProgressValue(memberToUpdate);

      // Add progressOfPerson to the memberToUpdate object
      memberToUpdate.progressOfPerson = memberProgress;

      // Now send the updated member with progress included
      const response = await axios.put(
        `${apiUrl}/cycles/${cycleId}/members/${cyclememberId}`,
        memberToUpdate,
        {
          headers: {
            'x-auth-token': localStorage.getItem('token'),
            'Content-Type': 'application/json',
          },
        },
      );

      // Update the local state with the response data
      setCycle((prevCycle) => ({
        ...prevCycle,
        cycleMembers: prevCycle.cycleMembers.map((member) =>
          member._id === cyclememberId
            ? { ...member, progressOfPerson: memberProgress }
            : member,
        ),
      }));
    } catch (error) {
      setToastMessage('Failed to save changes');
      setToastType('error');
      setShowToast(true);
    }
  }, 1000);

  const handleSaveCompletedDays = (
    cyclememberId,
    goalId,
    subtask,
    completedDates,
  ) => {
    //  calculateAllProgress(cyclememberId, goalId, subtask._id);
    // Store selected days in separate state for persistence
    setCompletedDates((prev) => ({
      ...prev,
      [subtask._id]: completedDates,
    }));

    // Update cycle state
    setCycle((prevCycle) => {
      const newCycle = {
        ...prevCycle,
        cycleMembers: prevCycle.cycleMembers.map((member) => {
          if (member._id === selectedMember._id) {
            return {
              ...member,
              goals: member.goals.map((goal) => {
                const updatedSubtasks = goal.subtasks.map((existingSubtask) => {
                  if (existingSubtask._id === subtask._id) {
                    return {
                      ...existingSubtask,
                      completedDays: completedDates, // Update completedDays instead of frequency.days
                      lastUpdated: new Date().toISOString(),
                    };
                  }
                  return existingSubtask;
                });

                return {
                  ...goal,
                  subtasks: updatedSubtasks,
                  lastUpdated: new Date().toISOString(),
                };
              }),
              lastUpdated: new Date().toISOString(),
            };
          }
          return member;
        }),
      };

      // Get updated member and save
      const updatedMember = newCycle.cycleMembers.find(
        (member) => member._id === selectedMember._id,
      );

      if (updatedMember) {
        debouncedSaveCalendarDates(
          selectedMember._id,
          updatedMember,
          apiUrl,
          cycleId,

          { setToastMessage, setToastType, setShowToast },
        );
      }

      // Trigger progress recalculation after state update
      setProgressUpdateData({
        memberId: cyclememberId,
        goalId: goalId,
        subtaskId: subtask._id,
      });
      setNeedsProgressUpdate(true);
      // Show toast with appropriate message based on number of days
      const daysCount = completedDates.length;
      setToastMessage(
        daysCount === 0
          ? 'All days cleared'
          : daysCount === 1
          ? '1 day marked complete'
          : `${daysCount} days marked complete`,
      );
      setToastType('success');
      setShowToast(true);

      return newCycle;
    });
  };
  // 5. Refactor calculation functions to be more robust
  const calculateSubtaskProgressValue = (subtaskId, memberData) => {
    // Find the subtask in the provided member data
    let foundSubtask = null;
    for (const goal of memberData.goals) {
      foundSubtask = goal.subtasks.find((subtask) => subtask._id === subtaskId);
      if (foundSubtask) break;
    }

    if (!foundSubtask) return 0;

    // Calculate progress based on frequency type
    if (foundSubtask.frequency.type === 'one-off') {
      return foundSubtask.isCompleted ? 100 : 0;
    } else if (foundSubtask.frequency.type === 'daily') {
      const startDate = new Date(cycle.startDate);
      const endDate = new Date(cycle.endDate);
      const timeDiff = endDate - startDate;
      const totalDays = Math.floor(timeDiff / (1000 * 60 * 60 * 24)) + 1;
      return ((foundSubtask.completedDays?.length || 0) / totalDays) * 100;
    } else if (foundSubtask.frequency.type === 'custom') {
      return (
        ((foundSubtask.completedDays?.length || 0) /
          (foundSubtask.frequency.days?.length || 1)) *
        100
      );
    }

    return 0;
  };

  const calculateGoalProgressValue = (goal) => {
    if (!goal.subtasks || goal.subtasks.length === 0) return 0;

    const totalProgress = goal.subtasks.reduce((acc, subtask) => {
      return acc + (subtask.subtaskProgress || 0);
    }, 0);

    return parseFloat((totalProgress / goal.subtasks.length).toFixed(2));
  };

  const calculateMemberProgressValue = (memberData) => {
    if (!memberData.goals || memberData.goals.length === 0) return 0;

    const totalProgress = memberData.goals.reduce((acc, goal) => {
      return acc + (goal.progress || 0);
    }, 0);

    return parseFloat((totalProgress / memberData.goals.length).toFixed(2));
  };

  // Modified handleSubtaskChange
  const handleSubtaskChange = (
    cyclememberId,
    goalId,
    subtaskId,
    isCompleted,
  ) => {
    // Update UI immediately
    setCycle((prevCycle) => {
      const newCycle = {
        ...prevCycle,
        cycleMembers: prevCycle.cycleMembers.map((member) => {
          if (member._id === cyclememberId) {
            return {
              ...member,
              goals: member.goals.map((goal) => {
                if (goal._id === goalId) {
                  return {
                    ...goal,
                    subtasks: goal.subtasks.map((subtask) => {
                      if (subtask._id === subtaskId) {
                        return {
                          ...subtask,
                          isCompleted: !isCompleted, // Toggle the state
                          lastUpdated: new Date().toISOString(), // Update the lastUpdated timestamp
                        };
                      }
                      return subtask;
                    }),
                    lastUpdated: new Date().toISOString(), // Update goal's lastUpdated
                  };
                }
                return goal;
              }),
              lastUpdated: new Date().toISOString(), // Update member's lastUpdated
            };
          }
          return member;
        }),
      };

      // Get updated member and save
      const updatedMember = newCycle.cycleMembers.find(
        (member) => member._id === cyclememberId,
      );

      if (updatedMember) {
        // Call debounced save with all necessary parameters
        debouncedSave(cyclememberId, updatedMember, apiUrl, cycleId, {
          setToastMessage,
          setToastType,
          setShowToast,
        });
      }
      // Show immediate feedback toast
      setToastMessage(isCompleted ? 'Task unmarked' : 'Task completed ✓');
      setToastType(isCompleted ? 'info' : 'success');
      setShowToast(true);

      setProgressUpdateData({
        memberId: cyclememberId,
        goalId: goalId,
        subtaskId: subtaskId,
      });
      setNeedsProgressUpdate(true);
      return newCycle;
    });

    // Track changes for any additional state management if needed
    const key = `${cyclememberId}-${goalId}-${subtaskId}`;
    setSubtaskChanges((prev) => ({
      ...prev,
      [key]: !isCompleted,
    }));

    // Optional: Update any progress calculations or other dependent state
  };

  const CustomProgressBar = ({ progress }) => {
    const circlePosition = `${progress}%`; // Calculate position for the circle

    return (
      <div className="progress-container">
        <div className="progress-bar" />
        <div
          className="progress-fill"
          style={{ width: circlePosition }} // Width based on progress
        />
        <div
          className="progress-circle"
          style={{ left: circlePosition }} // Position based on progress
        />
      </div>
    );
  };
  useEffect(() => {
    setHasUnsavedChanges(Object.keys(subtaskChanges).length > 0);
  }, [subtaskChanges]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (hasUnsavedChanges) {
        const message =
          'You have unsaved changes. Are you sure you want to leave?';
        event.returnValue = message; // Standard for most browsers
        return message; // For some older browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {}, [plannedDates, cycle]);

  function formatDateWithOrdinal(date) {
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'long' });
    const suffix = getDaySuffix(day);
    return `${day}${suffix} ${month}`;
  }

  function getDaySuffix(day) {
    if (day >= 11 && day <= 13) {
      return 'th';
    }
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  const [selectedMember, setSelectedMember] = useState(null);
  const [tasks, setTasks] = useState({});
  const [showCalendar, setShowCalendar] = useState(false);
  const handleShowModal = (cyclemember) => {
    setSelectedMember(cyclemember);
    const initialTasks = cyclemember.goals.reduce((acc, goal) => {
      goal.subtasks.forEach((subtask) => {
        acc[subtask._id] = subtask.isCompleted;
      });
      return acc;
    }, {});
    setTasks(initialTasks);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedMember(null);
  };

  useEffect(() => {
    // Update selectedMember whenever cycle changes
    const updatedMember = cycle?.cycleMembers.find(
      (member) => member._id === selectedMember?._id, // Use optional chaining
    );
    if (updatedMember) {
      setSelectedMember(updatedMember);
    }
  }, [cycle]); // Dependency on `cycle`

  const handleTaskToggle = (subtaskId) => {
    setTasks((prevTasks) => ({
      ...prevTasks,
      [subtaskId]: !prevTasks[subtaskId],
    }));
  };

  const formatCyclePeriod = () => {
    const startDate = new Date(cycle.startDate);
    const endDate = new Date(cycle.endDate);

    const formattedStartDate = formatDateWithOrdinal(startDate);
    const formattedEndDate = formatDateWithOrdinal(endDate);

    return ` ${formattedStartDate} - ${formattedEndDate}`;
  };

  useEffect(() => {
    const fetchCycle = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${apiUrl}/cycles/${groupId}/${cycleId}`,
          {
            headers: { 'x-auth-token': token },
          },
        );

        setCycle(response.data);
        console.log('response.data:: ', response.data);
        // Initialize selected dates and completed dates
        const initialPlannedDates = {};

        const initialCompletedDates = {};

        response.data.cycleMembers.forEach((member) => {
          member.goals.forEach((goal) => {
            goal.subtasks.forEach((subtask) => {
              // For custom frequency, use frequency.days as selectable dates
              if (subtask.frequency.type === 'custom') {
                initialPlannedDates[subtask._id] = subtask.frequency.days.map(
                  (date) => new Date(date),
                );
              }

              // Set completed days
              if (subtask.completedDays?.length > 0) {
                initialCompletedDates[subtask._id] = subtask.completedDays.map(
                  (date) => new Date(date),
                );
              }
            });
          });
        });

        setPlannedDates(initialPlannedDates);
        setCompletedDates(initialCompletedDates);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch data');
        setLoading(false);
      }
    };

    fetchCycle();
  }, [cycleId, groupId]);

  useEffect(() => {
    if (cycle) {
      const initialTasks = {};
      cycle.cycleMembers.forEach((member) =>
        member.goals.forEach((goal) =>
          goal.subtasks.forEach((subtask) => {
            initialTasks[subtask._id] = subtask.isCompleted;
          }),
        ),
      );
      setTasks(initialTasks);
    }
  }, [cycle]);

  const displayToast = (message, type) => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const generateAvatar = (username) => {
    // Encode the username to handle special characters
    const encodedUsername = encodeURIComponent(username);

    // Use the pixel-art style, but you can change this to any other style
    const style = 'pixel-art';

    // Construct the URL
    const avatarUrl = `https://api.dicebear.com/9.x/${style}/svg?seed=${encodedUsername}`;

    return avatarUrl;
  };

  const getLastUpdatedText = (lastUpdated) => {
    if (!lastUpdated) return ''; // Handle missing lastUpdated case

    const parsedDate = new Date(lastUpdated);
    if (isNaN(parsedDate)) {
      console.error('Invalid date format for lastUpdated:', lastUpdated);
      return '';
    }

    const now = new Date();
    const diffInMs = now - parsedDate;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Last updated today';
    if (diffInDays === 1) return 'Last updated 1 day ago';
    return `Last updated ${diffInDays} days ago`;
  };

  const handleIconClick = (subtask, event) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft =
      window.pageXOffset || document.documentElement.scrollLeft;

    // Position the popover slightly below and to the right of the icon
    setPopoverPosition({
      top: rect.bottom + scrollTop - 298,
      left: rect.left + scrollLeft - 10 - 448, // Adjust this value as needed
    });

    setSelectedSubtask(subtask);
    setShowCalendar(true);
  };
  return (
    <>
      <Row className="main-container">
        <Col md={2} className="sidebar">
          <SideNav />
        </Col>
        <Col>
          {loading ? (
            <LoadingQuote />
          ) : (
            <div>
              <Helmet>
                <title>Review Cycle</title>
                <meta
                  name="description"
                  content="Asquad - accountability made easy"
                />
              </Helmet>

              <Toast
                onClose={() => setShowToast(false)}
                show={showToast}
                delay={3000}
                autohide
                className={`position-fixed bottom-0 start-50 translate-middle-x mb-4 ${
                  toastType === 'success'
                    ? 'bg-green-50 border-green-200'
                    : toastType === 'error'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-blue-50 border-blue-200'
                }`}
                style={{
                  minWidth: '300px',
                  zIndex: 9999,
                }}
              >
                <Toast.Body
                  className={`d-flex align-items-center py-2 px-3 ${
                    toastType === 'success'
                      ? 'text-green-800'
                      : toastType === 'error'
                      ? 'text-red-800'
                      : 'text-blue-800'
                  }`}
                >
                  {/* Icon based on toast type */}
                  <span className="me-2">
                    {toastType === 'success' ? (
                      <svg
                        className="w-5 h-5 text-green-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : toastType === 'error' ? (
                      <svg
                        className="w-5 h-5 text-red-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5 text-blue-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </span>
                  {toastMessage}
                </Toast.Body>
              </Toast>

              <Row className="review-row">
                <div className="review-header">
                  <p>{groupDetails && groupDetails.groupname}</p>
                </div>
                <div className="period-section-header">
                  <div className="icon-calendar">
                    <LuCalendarClock />
                  </div>

                  <p className="period-title">{formatCyclePeriod(cycle)}</p>
                </div>

                {cycle?.cycleMembers.map((cyclemember) => {
                  const getMostRecentLastUpdated = (goals) => {
                    let latestDate = null;

                    goals.forEach((goal) => {
                      // Check the lastUpdated at the goal level
                      if (
                        !latestDate ||
                        new Date(goal.lastUpdated) > new Date(latestDate)
                      ) {
                        latestDate = goal.lastUpdated;
                      }

                      // Check each subtask's lastUpdated date
                      goal.subtasks.forEach((subtask) => {
                        if (
                          !latestDate ||
                          new Date(subtask.lastUpdated) > new Date(latestDate)
                        ) {
                          latestDate = subtask.lastUpdated;
                        }
                      });
                    });

                    return latestDate;
                  };

                  const mostRecentLastUpdated = getMostRecentLastUpdated(
                    cyclemember.goals,
                  );
                  const lastUpdatedText = getLastUpdatedText(
                    mostRecentLastUpdated,
                  );
                  return (
                    <Col
                      xs={12}
                      sm={6}
                      md={4}
                      lg={4}
                      key={cyclemember._id}
                      className="d-flex  justify-content-center"
                    >
                      <Card className="text-center shadow-sm review-card mx-auto mx-lg-0">
                        <Card.Header className="d-flex align-items-center review-card-header">
                          {/* Example avatar */}
                          <div className="avatar-gradient me-3">
                            <img
                              src={
                                cyclemember.user.profileImageUrl ||
                                generateAvatar(cyclemember.user.name)
                              }
                              alt="Avatar"
                              className="rounded-circle"
                              width="40"
                              height="40"
                            />
                          </div>
                          <span className="review-username">
                            {cyclemember.user.name}
                          </span>
                        </Card.Header>

                        <Card.Body className="review-card-body">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="progress-text">Progress</span>
                            <span className="percentage-text">
                              {cyclemember.progressOfPerson}%
                            </span>
                          </div>
                          <CustomProgressBar
                            progress={cyclemember.progressOfPerson}
                          />

                          <div className="d-flex justify-content-between align-items-center text-muted review-footer">
                            <span className="last-updated-text">
                              {lastUpdatedText}
                            </span>
                            <span
                              className="review-button"
                              onClick={() => handleShowModal(cyclemember)}
                              style={{ cursor: 'pointer' }}
                            >
                              Review
                            </span>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
              {/* Modal Popup */}

              <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                  <Modal.Title>Task Checklist</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  {selectedMember ? (
                    selectedMember.goals.map((goal) => (
                      <div
                        key={goal._id}
                        className="mb-3 bg-white/60 transition-all duration-300"
                      >
                        <h5 className="text-base font-semibold text-gray-800 mb-2 flex items-center">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 mr-2"></span>
                          {goal.goalName}
                        </h5>

                        {goal.subtasks.map((subtask) => (
                          <div
                            key={subtask._id}
                            className="group flex items-center py-1.5 px-2 hover:bg-white/80 rounded-md transition-all duration-200"
                          >
                            {subtask.frequency.type === 'daily' ||
                            subtask.frequency.type === 'custom' ? (
                              <button
                                onClick={(event) =>
                                  handleIconClick(subtask, event)
                                }
                                className="flex items-center justify-center w-4 h-4 rounded-sm
                         text-gray-400 hover:text-green-600
                         transition-all duration-200 group-hover:scale-110"
                              >
                                <i className="fas fa-calendar-alt text-[15px]"></i>
                              </button>
                            ) : (
                              <div className="relative flex items-center">
                                <input
                                  type="checkbox"
                                  checked={subtask.isCompleted}
                                  onChange={() =>
                                    handleSubtaskChange(
                                      selectedMember._id,
                                      goal._id,
                                      subtask._id,
                                      subtask.isCompleted,
                                    )
                                  }
                                  className="w-4 h-4 rounded-sm border-2 border-gray-300 
                   accent-emerald-500 !text-emerald-500 
                   transition-all duration-200
                   focus:ring-2 focus:ring-emerald-500/20 focus:ring-offset-0
                   checked:border-emerald-500 checked:bg-emerald-500
                   hover:border-emerald-500/50 cursor-pointer"
                                  style={{
                                    accentColor: '#2a9d29', // emerald-500 color code
                                    backgroundColor: subtask.isCompleted
                                      ? '#2a9d29'
                                      : 'white',
                                    color: 'white',
                                  }}
                                />
                              </div>
                            )}

                            <span
                              className={`ml-3 text-sm transition-all duration-200
            ${
              subtask.isCompleted
                ? 'text-gray-400 line-through'
                : 'text-gray-700 group-hover:text-gray-900'
            }`}
                            >
                              {subtask.subTaskName}
                            </span>
                          </div>
                        ))}
                      </div>
                    ))
                  ) : (
                    <p>No tasks available</p>
                  )}
                  {showCalendar && selectedSubtask && (
                    <div
                      className="custom-popover-container"
                      style={{
                        position: 'absolute',
                        top: `${popoverPosition.top}px`,
                        left: `${popoverPosition.left}px`,
                        zIndex: 1060,
                      }}
                    >
                      <ReviewCalendarPopover
                        startDate={cycle.startDate}
                        endDate={cycle.endDate}
                        selectedSubtask={selectedSubtask}
                        initialPlannedDates={
                          plannedDates[selectedSubtask._id] || []
                        }
                        onSelectDays={(days) => {
                          // Find the goal that contains the selectedSubtask
                          const goal = selectedMember.goals.find((g) =>
                            g.subtasks.some(
                              (s) => s._id === selectedSubtask._id,
                            ),
                          );

                          if (goal) {
                            handleSaveCompletedDays(
                              selectedMember._id,
                              goal._id,
                              selectedSubtask,
                              days,
                            );
                          } else {
                            console.error(
                              'Goal not found for the selected subtask',
                            );
                          }
                        }}
                        onClose={() => setShowCalendar(false)}
                      />
                    </div>
                  )}
                </Modal.Body>
                <Modal.Footer></Modal.Footer>
              </Modal>
            </div>
          )}
        </Col>
      </Row>
    </>
  );
};

export default GroupReviewOverview;

//Comments: How does ReviewCalendarPopover work?
//The ReviewCalendarPopover component is a modal that allows the user to select dates for a subtask.
//There are two things to note that should happen, plannedDates and completedDates.
//PlannedDates are the dates that the user has planned to complete the subtask.
//CompletedDates are the dates that the user has completed the subtask.
//The ReviewCalendarPopover component should be able to handle the plannedDates and completedDates.
//The Planned Dates should only be the selectable dates
//The completed dates will be green when marked as completed.

// when the calendar/review component first loads it should take the
// frequency.days and set to planneddates
// when a user clicks on a day (if it is a planneddate)
// ) it will trigger a function that will add that date to the completeddates

//subtask progress
// The subtsk progress if it is one off - should be 100% if completed
// if it is daily - the progress should be the number of days completed divided by the total number of days  between start and end date (inclusive)
// if it is custom - the progress should be the number of days completed divided by the total number of days planned

//Lets say we have 3 goals with 3 subtasks each

//We then have to calculate the progress of each subtask and devide by the number of subtasks for each goal we get goal progress
//then we have to calculate the  total progress of all goals and devide by the number of goals we get the total progress of the person

//what functions should we have to do this
//1. calculate the total progress of a person
//2. calculate the total progress of a goal
//3. calculate the total progress of a subtask

//whenever a day is completed we have to update the subtask progress, goal progress and total progress of the person
//whenever a subtask is completed we have to update the subtask progress, goal progress and total progress of the person
